import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ICardConfiguration, IExtendedPanel,
  IFormConfiguration,
  IPanelConfiguration
} from '@ispm/therapies/interfaces/section-configuration.interface';
import { get, set } from 'lodash';
import { ItherapyMatch } from '@ispm/therapies/interfaces/therapy-match.interface';
import { IclinicalTrials } from '@ispm/therapies/interfaces/clinical-trials.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { DynamicFormItem } from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import { ITEMS_TYPES } from '@ispm/dynamic-form/constants/ItemsTypes';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import { ConfigurationStoreHandler } from '../../../configuration/services/configuration-store-handler.service';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import {takeUntil, tap} from "rxjs/operators";

@Component({
  selector: 'gen-treatment-information',
  templateUrl: './treatment-information.component.html',
  styleUrls: ['./treatment-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('TreatmentInformation')
export class TreatmentInformationComponent implements OnInit, OnDestroy, IWidgetItem {
  public additionalData: any = { };
  public updateValuesOn: string;
  public additionalSearchValues$: Observable<any>;
  public formItems: IdynamicFormItem[] = [];
  public shortFormItems: IdynamicFormItem[];
  public formId: string;

  public data$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private _resource: ItherapyMatch | IclinicalTrials;
  static readonly EMPTY_MESSAGE = 'Information not available';

  get staticEmptyMessage() {
    return TreatmentInformationComponent.EMPTY_MESSAGE;
  }

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    Object.assign(this, widgetConfig.widgetPayload);
    this.init();
  }

  set widgetData(widgetData: any) {
    const inData: any[] = Array.isArray(widgetData) ? widgetData : [widgetData];
    inData.forEach(tabData =>{
      this.therapiesService.filterTherapiesByCondition(tabData); //Should get IExtendedTab
    });
    this.data = inData;
    this._changeDetector.markForCheck();
  }

  @Input()
  public indexProperty: string = 'id';

  @Input()
  public customEmptyMessage: string;

  @Input()
  public panelConfiguration: IExtendedPanel | IPanelConfiguration | ICardConfiguration | IFormConfiguration;

  @Output()
  private treatmentInfoTabChanged: EventEmitter<void> = new EventEmitter<void>();

  @Input()
  public set data(resources: (any | any[])) {
    this.data$.next(Array.isArray(resources) ? resources : [resources]);
    this.treatmentInfoTabChanged.emit();
  }

  private destroyer$: Subject<void> = new Subject<void>();

  constructor(
    private therapiesAC: TherapiesActionCreatorService,
    private configurationAC: ConfigurationStoreHandler,
    private _changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
    private therapiesService: TherapiesService
  ) {
    this.additionalSearchValues$ = this.configurationAC.getCurrentConfig();
  }

  ngOnInit() {
    this.init();
  }

  init() {
    this.indexProperty = this.panelConfiguration.indexProperty || this.indexProperty ;
    switch (this.panelConfiguration.type) {
      case 'form':
        this.formItems = (<IFormConfiguration> this.panelConfiguration).formConfig;
        break;
      case 'card':
        this.buildFormConfigurationFromFields();
        break;
      default:
        break;
    }
  }

  onFormChange(form: any) {
    let storedValue;

    if ((<IFormConfiguration> this.panelConfiguration).fieldsToPreserve) {
      storedValue = { };
      (<IFormConfiguration> this.panelConfiguration).fieldsToPreserve.forEach( field => storedValue[field] = form[field]);
    } else {
      storedValue = form;
    }

    this.additionalData = storedValue;
    this.therapiesAC.updateStoredTherapies('formChange', this.additionalData, this.indexProperty);
  }

  buildFormConfigurationFromFields(): void {
    this.formItems = [];

    // get data by test discipline and configuration
    if (get(this.panelConfiguration, 'additionalData.formIdByData')) {
      this.data$.pipe(
        takeUntil(this.destroyer$.asObservable()),
        tap(data => this.defineFormIdByData(data))
      ).subscribe();
    }
    else {
      if (get(this.panelConfiguration, 'additionalData.formId')) {
        this.formId = this.panelConfiguration.additionalData['formId'];
      } else {
        this.panelConfiguration.fields.forEach(field => {
          const formItem = Object.assign({
            name: field.path || field.linkPath,
            type: ITEMS_TYPES.TEXT,
            showAsLink: !!field.linkPath,
            showLinkSuffix: false
          }, field);

          this.formItems.push(DynamicFormItem.from(formItem));
        });
        this.shortFormItems = this.formItems.slice(0, (<ICardConfiguration> this.panelConfiguration).fieldsAmount || 3);
      }
    }
  }

  trackByFn(field: string, fallbackField?: string) {
    return function (index, item) {
      if (!item) {
        return index;
      } else if (get(item, field)) {
        return get(item, field);
      } else if (item[fallbackField]) {
        return item[fallbackField];
      } else {
        return index;
      }
    };
  }

  getItemField(item, field: IPanelConfiguration) {
    return get(item, field.path, null);
  }

  // data should contain items with the same fields. we can't show drugs and therapies at the same time
  defineFormIdByData(currentData: any[]) {
    // where to get current discipline ?
    const formsData = get(this.panelConfiguration, 'additionalData.formIdByData');
    const defaultForm = get(this.panelConfiguration, 'additionalData.formId');

    if (Array.isArray(formsData)) {
      const description = formsData.find(_description =>
        (currentData.some(dataItem => get(dataItem, _description.path) === _description.value))
      );
      if (description) {
        this.formId = description.formId;
        return;
      }
    }

    this.formId = defaultForm;
  }

  getFieldRecursively(item, paths, index) {
    if (paths.length === index) {
      return item;
    }

    return index % 2 !== 0
      ? item.map(rec => this.getFieldRecursively(rec, paths, index + 1))
      : this.getFieldRecursively(item[paths[index]], paths, index + 1);
  }

  addToSelection(item) {
    let storeItem = item;

    if (storeItem.hasOwnProperty('id')) {
      switch (item.resourceType && item.resourceType.toUpperCase()) {
        case 'CLINICALTRIALMATCH':
          storeItem = {
            id: item.id,
            resourceType: item.resourceType,
            target: item.target,
            displayName: item.trialID
          };

          if (this.panelConfiguration.fields) {
            this.panelConfiguration.fields.forEach((field) => {
              const path = field.path || field.linkPath;
              set(storeItem, path, get(item, path));
            });
          }
          break;
        default:
          break;
      }

      this.setAdditionalDataForStoredObject(storeItem);

      if (this.updateValuesOn) {
        TherapiesActionCreatorService.SetStoreMetaForTreatment(storeItem, this.updateValuesOn);
      }

      this.therapiesAC.addTherapy(storeItem as any, this.indexProperty);
    }
  }

  removeFromSelection(item) {
    this.therapiesAC.removeTherapyFromPriority(item, this.indexProperty);
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private setAdditionalDataForStoredObject(storeItem) {
    Object.assign(storeItem, get(this.panelConfiguration, 'additionalData', { }));
    Object.assign(storeItem, this.additionalData);

    const additionalProperties = get(this.panelConfiguration, 'propertiesToStore', []) as Array<{ dist: string, source: string, fallback?: any}>;

    if (Array.isArray(additionalProperties)) {
      for (const property of additionalProperties) {
        const valueToSet = get(this._resource, property.source, property.fallback);
        set(storeItem, property.dist, valueToSet);
      }
    }
  }
}
